import { useState, type FormEvent } from "react";
import type { ApiError } from "../api/client";
import type { AthleteProfile, BestMark, TrainingZone } from "../api/types";
import {
  DISTANCE_PRESETS,
  EXPERIENCE_LABEL,
  formatDuration,
  formatPace,
  paceOf,
  parseDuration,
  parsePace,
  PREFERRED_TIME_LABEL,
  WEEKDAYS,
} from "../lib/format";
import { Button, FormError, SelectField, TextArea, TextField } from "./ui";
import { t } from "../i18n";

interface MarkRow {
  distance: string; // metros (preset) ou "custom"
  customKm: string;
  time: string;
  date: string;
  race: string;
}
interface ZoneRow {
  name: string;
  hrMin: string;
  hrMax: string;
  paceMin: string;
  paceMax: string;
}

const toStr = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));
const toInt = (s: string) => {
  const n = s.trim() === "" ? null : Number.parseInt(s, 10);
  return n === null || Number.isNaN(n) ? null : n;
};
const paceStr = (s: number | null) => (s ? formatPace(s).replace(" /km", "") : "");

function markToRow(m: BestMark): MarkRow {
  const preset = DISTANCE_PRESETS.find((p) => p.m === m.distance_m);
  return {
    distance: preset ? String(preset.m) : "custom",
    customKm: preset ? "" : String(m.distance_m / 1000).replace(".", ","),
    time: formatDuration(m.time_s),
    date: m.date ?? "",
    race: m.race ?? "",
  };
}

function zoneToRow(z: TrainingZone): ZoneRow {
  return {
    name: z.name,
    hrMin: toStr(z.hr_min_bpm),
    hrMax: toStr(z.hr_max_bpm),
    paceMin: paceStr(z.pace_min_s_km),
    paceMax: paceStr(z.pace_max_s_km),
  };
}

/**
 * Formulário do perfil esportivo.
 * mode="full": treinador/gestor (inclui FC e zonas). mode="self": o próprio atleta.
 * Monta o payload só com campos permitidos; a validação definitiva é sempre do backend.
 */
export function ProfileForm({
  profile,
  mode,
  onSubmit,
  onCancel,
}: {
  profile: AthleteProfile | null;
  mode: "full" | "self";
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
  onCancel?: () => void;
}) {
  const p = profile;
  const [experience, setExperience] = useState(p?.running_experience ?? "");
  const [since, setSince] = useState(p?.running_since ?? "");
  const [weeklyKm, setWeeklyKm] = useState(p?.usual_weekly_volume_m != null ? String(p.usual_weekly_volume_m / 1000).replace(".", ",") : "");
  const [days, setDays] = useState<number[]>(p?.preferred_days ?? []);
  const [time, setTime] = useState(p?.preferred_time ?? "");
  const [equipment, setEquipment] = useState(p?.equipment ?? "");
  const [history, setHistory] = useState(p?.training_history ?? "");
  const [goals, setGoals] = useState(p?.goals_text ?? "");
  const [notes, setNotes] = useState(p?.sport_notes ?? "");
  const [hrMax, setHrMax] = useState(toStr(p?.hr_max_bpm));
  const [hrRest, setHrRest] = useState(toStr(p?.hr_rest_bpm));
  const [hrLow, setHrLow] = useState(toStr(p?.hr_low_max_bpm));
  const [hrMod, setHrMod] = useState(toStr(p?.hr_moderate_max_bpm));
  const [marks, setMarks] = useState<MarkRow[]>((p?.best_marks ?? []).map(markToRow));
  const [zones, setZones] = useState<ZoneRow[]>((p?.training_zones ?? []).map(zoneToRow));
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  function build(): Record<string, unknown> | null {
    const errs: Record<string, string> = {};
    const km = weeklyKm.trim() ? Number(weeklyKm.replace(",", ".")) : null;
    if (km !== null && (Number.isNaN(km) || km < 0 || km > 500)) errs.weekly = t("Informe um volume entre 0 e 500 km.");

    const bestMarks = marks.map((m, i) => {
      const distance_m =
        m.distance === "custom" ? Math.round(Number(m.customKm.replace(",", ".")) * 1000) : Number(m.distance);
      const time_s = parseDuration(m.time);
      if (!distance_m || Number.isNaN(distance_m)) errs[`mark${i}`] = t("Informe a distância.");
      else if (time_s === null) errs[`mark${i}`] = t("Tempo no formato h:mm:ss ou mm:ss.");
      return { distance_m, time_s, date: m.date || null, race: m.race.trim() || null };
    });

    const maxN = toInt(hrMax), restN = toInt(hrRest), lowN = toInt(hrLow), modN = toInt(hrMod);
    if (maxN !== null && (maxN < 100 || maxN > 240)) errs.hrMax = t("FC máxima entre 100 e 240 bpm.");
    if (restN !== null && (restN < 25 || restN > 120)) errs.hrRest = t("FC de repouso entre 25 e 120 bpm.");
    if (maxN !== null && restN !== null && restN >= maxN) errs.hrRest = t("A FC de repouso deve ser menor que a máxima.");
    if ((lowN === null) !== (modN === null)) errs.hrBands = t("Informe os dois limites (baixa e moderada) ou deixe os dois em branco.");
    else if (lowN !== null && modN !== null) {
      if (lowN < 30 || modN > 240) errs.hrBands = t("Limites entre 30 e 240 bpm.");
      else if (lowN >= modN) errs.hrBands = t("O limite da intensidade baixa deve ser menor que o da moderada.");
      else if (maxN !== null && modN >= maxN) errs.hrBands = t("O limite da moderada deve ser menor que a FC máxima.");
    }

    const trainingZones = zones.map((z, i) => {
      const pmin = z.paceMin.trim() ? parsePace(z.paceMin) : null;
      const pmax = z.paceMax.trim() ? parsePace(z.paceMax) : null;
      if (!z.name.trim()) errs[`zone${i}`] = t("Dê um nome à zona.");
      if ((z.paceMin.trim() && pmin === null) || (z.paceMax.trim() && pmax === null))
        errs[`zone${i}`] = t("Ritmo no formato m:ss (ex.: 5:30).");
      return {
        name: z.name.trim(),
        hr_min_bpm: toInt(z.hrMin),
        hr_max_bpm: toInt(z.hrMax),
        pace_min_s_km: pmin,
        pace_max_s_km: pmax,
      };
    });

    setLocalErrors(errs);
    if (Object.keys(errs).length) return null;

    const payload: Record<string, unknown> = {
      running_since: since || null,
      usual_weekly_volume_m: km === null ? null : Math.round(km * 1000),
      preferred_days: days,
      preferred_time: time || null,
      equipment,
      training_history: history,
      goals_text: goals,
      best_marks: bestMarks,
      hr_max_bpm: maxN,
      hr_rest_bpm: restN,
      hr_low_max_bpm: lowN,
      hr_moderate_max_bpm: modN,
    };
    if (mode === "full") {
      Object.assign(payload, {
        running_experience: experience || null,
        training_zones: trainingZones,
        sport_notes: notes,
      });
    }
    return payload;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const payload = build();
    if (!payload) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  const apiFields = error?.fieldErrors() ?? {};

  return (
    <form className="form" onSubmit={submit} noValidate>
      {error && <FormError error={error} />}
      <div className="form-grid">
        {mode === "full" && (
          <SelectField label={t("Experiência")} value={experience} onChange={(e) => setExperience(e.target.value)}>
            <option value="">{t("Não informada")}</option>
            {Object.entries(EXPERIENCE_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </SelectField>
        )}
        <TextField label={t("Corre desde")} type="date" value={since} onChange={(e) => setSince(e.target.value)}
          error={apiFields["profile.running_since"] ?? apiFields.running_since} />
        <TextField
          label={t("Volume semanal habitual (km)")}
          inputMode="decimal"
          value={weeklyKm}
          onChange={(e) => setWeeklyKm(e.target.value)}
          error={localErrors.weekly}
        />
        <SelectField label={t("Horário preferido")} value={time} onChange={(e) => setTime(e.target.value)}>
          <option value="">{t("Não informado")}</option>
          {Object.entries(PREFERRED_TIME_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </SelectField>
      </div>

      <fieldset>
        <legend>{t("Dias preferidos para treinar")}</legend>
        <div className="day-picker">
          {WEEKDAYS.map((d, i) => (
            <label key={d}>
              <input
                type="checkbox"
                checked={days.includes(i)}
                onChange={(e) => setDays(e.target.checked ? [...days, i].sort() : days.filter((x) => x !== i))}
              />
              <span>{d}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="form-grid">
        <TextArea label={t("Objetivos")} value={goals} onChange={(e) => setGoals(e.target.value)} />
        <TextArea label={t("Histórico de treinamento")} value={history} onChange={(e) => setHistory(e.target.value)} />
      </div>
      <TextField label={t("Equipamentos")} hint={t("Tênis, relógio, cinta cardíaca…")} value={equipment}
        onChange={(e) => setEquipment(e.target.value)} />

      <fieldset>
        <legend>{t("Melhores marcas conhecidas")}</legend>
        <div className="stack" style={{ gap: 10 }}>
          {marks.length === 0 && <p className="muted">{t("Nenhuma marca registrada.")}</p>}
          {marks.map((m, i) => {
            const set = (patch: Partial<MarkRow>) => setMarks(marks.map((x, j) => (j === i ? { ...x, ...patch } : x)));
            const tp = parseDuration(m.time);
            const dist = m.distance === "custom" ? Number(m.customKm.replace(",", ".")) * 1000 : Number(m.distance);
            return (
              <div key={i} className="panel panel-body" style={{ padding: 14 }}>
                <div className="form-grid">
                  <SelectField label={t("Distância")} value={m.distance} onChange={(e) => set({ distance: e.target.value })}>
                    {DISTANCE_PRESETS.map((d) => (
                      <option key={d.m} value={d.m}>
                        {d.label}
                      </option>
                    ))}
                    <option value="custom">{t("Outra")}</option>
                  </SelectField>
                  {m.distance === "custom" && (
                    <TextField label={t("Distância (km)")} inputMode="decimal" value={m.customKm}
                      onChange={(e) => set({ customKm: e.target.value })} />
                  )}
                  <TextField label={t("Tempo")} placeholder="h:mm:ss" value={m.time} onChange={(e) => set({ time: e.target.value })}
                    hint={tp && dist ? t("Ritmo médio {pace}", { pace: formatPace(paceOf(tp, dist)) }) : undefined} error={localErrors[`mark${i}`]} />
                  <TextField label={t("Data")} type="date" value={m.date} onChange={(e) => set({ date: e.target.value })} />
                  <TextField label={t("Prova")} value={m.race} onChange={(e) => set({ race: e.target.value })} />
                </div>
                <div style={{ marginTop: 10 }}>
                  <Button size="sm" variant="ghost" onClick={() => setMarks(marks.filter((_, j) => j !== i))}>
                    {t("Remover marca")}
                  </Button>
                </div>
              </div>
            );
          })}
          <div>
            <Button size="sm" onClick={() => setMarks([...marks, { distance: "5000", customKm: "", time: "", date: "", race: "" }])}>
              {t("Adicionar marca")}
            </Button>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>{t("Frequência cardíaca e faixas de intensidade")}</legend>
        <p className="muted">
          {t("Com a sua FC e as faixas, o EAPace calcula quanto tempo de cada treino foi em intensidade baixa, moderada e alta (gráfico de evolução). Sem as faixas, usa 82% e 88% da FC máxima.")}
        </p>
        <div className="form-grid">
          <TextField label={t("FC máxima (bpm)")} inputMode="numeric" value={hrMax} onChange={(e) => setHrMax(e.target.value)}
            error={localErrors.hrMax ?? apiFields["hr_max_bpm"]} />
          <TextField label={t("FC de repouso (bpm)")} inputMode="numeric" value={hrRest} onChange={(e) => setHrRest(e.target.value)}
            error={localErrors.hrRest ?? apiFields["hr_rest_bpm"]} />
          <TextField label={t("Intensidade baixa: até (bpm)")} inputMode="numeric" value={hrLow} onChange={(e) => setHrLow(e.target.value)}
            error={localErrors.hrBands ?? apiFields["hr_low_max_bpm"]} />
          <TextField label={t("Intensidade moderada: até (bpm)")} inputMode="numeric" value={hrMod} onChange={(e) => setHrMod(e.target.value)}
            error={apiFields["hr_moderate_max_bpm"]}
            hint={toInt(hrLow) && toInt(hrMod) ? t("Alta: acima de {hrMod} bpm.", { hrMod: toInt(hrMod) }) : t("Acima deste valor conta como alta.")} />
        </div>
        <div style={{ marginTop: 8 }}>
          <Button
            size="sm"
            disabled={toInt(hrMax) === null}
            onClick={() => {
              const mx = toInt(hrMax);
              if (mx === null) return;
              setHrLow(String(Math.round(mx * 0.82)));
              setHrMod(String(Math.round(mx * 0.88)));
            }}
          >
            {t("Sugerir pela FC máxima (82% / 88%)")}
          </Button>
        </div>
      </fieldset>

      {mode === "full" && (
        <>
          <fieldset>
            <legend>{t("Zonas de treinamento")}</legend>
            <div className="stack" style={{ gap: 10 }}>
              {zones.length === 0 && <p className="muted">{t("Nenhuma zona configurada.")}</p>}
              {zones.map((z, i) => {
                const set = (patch: Partial<ZoneRow>) => setZones(zones.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                return (
                  <div key={i} className="panel panel-body" style={{ padding: 14 }}>
                    <div className="form-grid">
                      <TextField label={t("Nome")} value={z.name} onChange={(e) => set({ name: e.target.value })} error={localErrors[`zone${i}`]} />
                      <TextField label={t("FC mín.")} inputMode="numeric" value={z.hrMin} onChange={(e) => set({ hrMin: e.target.value })} />
                      <TextField label={t("FC máx.")} inputMode="numeric" value={z.hrMax} onChange={(e) => set({ hrMax: e.target.value })} />
                      <TextField label={t("Ritmo mais rápido")} placeholder="m:ss" value={z.paceMin} onChange={(e) => set({ paceMin: e.target.value })} />
                      <TextField label={t("Ritmo mais lento")} placeholder="m:ss" value={z.paceMax} onChange={(e) => set({ paceMax: e.target.value })} />
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <Button size="sm" variant="ghost" onClick={() => setZones(zones.filter((_, j) => j !== i))}>
                        {t("Remover zona")}
                      </Button>
                    </div>
                  </div>
                );
              })}
              <div>
                <Button size="sm" onClick={() => setZones([...zones, { name: `Z${zones.length + 1}`, hrMin: "", hrMax: "", paceMin: "", paceMax: "" }])}>
                  {t("Adicionar zona")}
                </Button>
              </div>
            </div>
          </fieldset>
          <TextArea label={t("Observações esportivas")} hint={t("Visível para a equipe técnica e para o atleta. Não registre dados de saúde aqui.")}
            value={notes} onChange={(e) => setNotes(e.target.value)} />
        </>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {onCancel && (
          <Button onClick={onCancel} variant="ghost">
            {t("Cancelar")}
          </Button>
        )}
        <Button type="submit" variant="primary" busy={busy}>
          {t("Salvar perfil")}
        </Button>
      </div>
    </form>
  );
}
